import { blobCenter } from './blobGeometry';
import type { LocateMatch, LocateResult, ResultOrdering } from './LocateTypes';

/**
 * Spatial center used for scan-order sorting. The SAM2 contour is the source of
 * truth when present (axis-aligned center of the segment), falling back to the
 * detector box center when the match has no segment.
 */
function matchCenter(m: LocateMatch): { x: number; y: number } {
  return m.segment
    ? blobCenter(m.segment)
    : { x: m.detection.x, y: m.detection.y };
}

function getComparator(
  ordering: ResultOrdering,
): (a: LocateMatch, b: LocateMatch) => number {
  switch (ordering) {
    // Scan order — sort by distance from a corner (derived center)
    case 'top-left-to-bottom-right':
      return (a, b) => {
        const ca = matchCenter(a);
        const cb = matchCenter(b);

        return ca.x + ca.y - (cb.x + cb.y);
      };

    case 'bottom-right-to-top-left':
      return (a, b) => {
        const ca = matchCenter(a);
        const cb = matchCenter(b);

        return cb.x + cb.y - (ca.x + ca.y);
      };

    case 'top-right-to-bottom-left':
      return (a, b) => {
        const ca = matchCenter(a);
        const cb = matchCenter(b);

        return cb.x - ca.x + (ca.y - cb.y);
      };

    case 'bottom-left-to-top-right':
      return (a, b) => {
        const ca = matchCenter(a);
        const cb = matchCenter(b);

        return ca.x - cb.x + (cb.y - ca.y);
      };

    // By property (descending)
    case 'confidence-desc':
      return (a, b) => b.detection.score - a.detection.score;

    case 'area-desc':
      return (a, b) => (b.segment?.area ?? 0) - (a.segment?.area ?? 0);

    // World-height ordering reads the projected 3D pose, which a 2D
    // `LocateMatch` does not carry. It is applied after projection by
    // `applyPostProjectionOrdering`, so at this stage it leaves the detection
    // order untouched rather than inventing a 2D proxy for height.
    case 'world-z-desc':
      return () => 0;
  }
}

/**
 * Sort matches by the given ordering strategy.
 * Returns a new sorted array (does not mutate the input).
 * When ordering is undefined, falls back to score descending (legacy behavior).
 */
export function sortBlobs(
  matches: LocateMatch[],
  ordering: ResultOrdering | undefined,
): LocateMatch[] {
  const effectiveOrdering: ResultOrdering = ordering ?? 'confidence-desc';

  if (matches.length <= 1) {
    return [...matches];
  }

  const sorted = [...matches];

  sorted.sort(getComparator(effectiveOrdering));

  return sorted;
}

/**
 * Apply the orderings that can only be evaluated once matches have been
 * projected into 3D poses.
 *
 * `sortBlobs` runs on 2D detections before projection and cannot see a match's
 * world height, so `runLocate` calls this on the projected `LocateResult[]`
 * after poses exist. Only `world-z-desc` needs it — every other ordering is
 * already settled by the pre-projection `sortBlobs`, so they pass through
 * unchanged and the projected order is preserved.
 *
 * Returns a new array (does not mutate the input). The sort is stable for equal
 * heights, so a `ManualPlane` projection — where every match shares the taught
 * plane's Z — keeps the pre-projection order.
 */
export function applyPostProjectionOrdering(
  results: LocateResult[],
  ordering: ResultOrdering | undefined,
): LocateResult[] {
  if (ordering !== 'world-z-desc') {
    return results;
  }

  // Highest world Z first — the top of a stack is picked before the layers
  // beneath it.
  return [...results].sort((a, b) => b.pose.z - a.pose.z);
}
